import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ALL_CHAINS, getAvailableChainIds, getPaymentUrl, isSameChainAndToken, ZERO_ADDRESS } from "../../../anyspend/index.js";
import { Button, toast } from "../../../global-account/react/index.js";
import { cn } from "../../../shared/utils/cn.js";
import { TokenSelector } from "@relayprotocol/relay-kit-ui";
import { Check, ChevronsUpDown, Copy, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { useAnyspendOrderAndTransactions } from "../hooks/useAnyspendOrderAndTransactions.js";
import { useCreateDepositFirstOrder } from "../hooks/useCreateDepositFirstOrder.js";
import { useOnOrderSuccess } from "../hooks/useOnOrderSuccess.js";
import { useWatchTransfer } from "../hooks/useWatchTransfer.js";
import { ChainTokenIcon } from "./common/ChainTokenIcon.js";
import { OrderDetails } from "./common/OrderDetails.js";
import { TransferResultScreen } from "./common/TransferResultScreen.js";
import { ChainWarningText, WarningText } from "./common/WarningText.js";
// Default source token: ETH on Base
const DEFAULT_ETH_ON_BASE = {
  chainId: 8453,
  address: "0x0000000000000000000000000000000000000000",
  symbol: "ETH",
  name: "Ethereum",
  decimals: 18,
  metadata: {
    logoURI: "https://assets.relay.link/icons/1/light.png"
  }
};
/**
 * A component for displaying QR code deposit functionality.
 * Creates a deposit_first order on mount and shows a QR code that can be scanned to deposit tokens.
 * Users can change the source token/chain using the TokenSelector.
 *
 * @example
 * <QRDeposit
 *   recipientAddress={userAddress}
 *   destinationToken={usdcArbitrumToken}
 *   destinationChainId={42161}
 *   onBack={() => setStep("select-chain")}
 *   onSuccess={(txHash) => console.log("Deposit complete:", txHash)}
 * />
 */
export function QRDeposit({
  mode = "modal",
  recipientAddress,
  sourceToken: sourceTokenProp,
  sourceChainId: sourceChainIdProp,
  destinationToken,
  destinationChainId,
  creatorAddress,
  depositContractConfig,
  onBack,
  onClose,
  onOrderCreated,
  onSuccess,
  classes
}) {
  const [copied, setCopied] = useState(false);
  const [orderId, setOrderId] = useState();
  const [globalAddress, setGlobalAddress] = useState();
  const orderCreatedRef = useRef(false);
  const [transferResult, setTransferResult] = useState(null);
  // Source token/chain as state (can be changed by user)
  const [sourceChainId, setSourceChainId] = useState(sourceChainIdProp ?? 8453);
  const [sourceToken, setSourceToken] = useState(sourceTokenProp ?? DEFAULT_ETH_ON_BASE);
  // Check if this is a pure transfer (same chain and token)
  const isPureTransfer = isSameChainAndToken(sourceChainId, sourceToken.address, destinationChainId, destinationToken.address);
  // Watch for pure transfers (same chain and token)
  const {
    isWatching: isWatchingTransfer
  } = useWatchTransfer({
    address: recipientAddress,
    chainId: sourceChainId,
    tokenAddress: sourceToken.address,
    tokenDecimals: sourceToken.decimals,
    enabled: isPureTransfer && !transferResult,
    onTransferDetected: result => {
      setTransferResult(result);
      onSuccess?.();
    }
  });
  // Handle token selection from TokenSelector
  const handleTokenSelect = newToken => {
    const token = {
      address: newToken.address,
      chainId: newToken.chainId,
      decimals: newToken.decimals,
      metadata: {
        logoURI: newToken.logoURI
      },
      name: newToken.name,
      symbol: newToken.symbol
    };
    // Reset order state when token changes
    setOrderId(undefined);
    setGlobalAddress(undefined);
    orderCreatedRef.current = false;
    setTransferResult(null);
    // Update token and chain
    setSourceChainId(newToken.chainId);
    setSourceToken(token);
  };
  // Create order hook
  const {
    createOrder,
    isCreatingOrder
  } = useCreateDepositFirstOrder({
    onSuccess: data => {
      const newOrderId = data.data.id;
      const newGlobalAddress = data.data.globalAddress;
      setOrderId(newOrderId);
      setGlobalAddress(newGlobalAddress);
      onOrderCreated?.(newOrderId);
    },
    onError: error => {
      console.error("Failed to create deposit order:", error);
      toast.error("Failed to create deposit order: " + error.message);
    }
  });
  // Fetch order status
  const {
    orderAndTransactions: oat
  } = useAnyspendOrderAndTransactions(orderId);
  // Create order on mount (skip for pure transfers)
  useEffect(() => {
    if (orderCreatedRef.current) return;
    if (isPureTransfer) return; // Skip order creation for pure transfers
    orderCreatedRef.current = true;
    createOrder({
      recipientAddress,
      srcChain: sourceChainId,
      dstChain: destinationChainId,
      srcToken: sourceToken,
      dstToken: destinationToken,
      creatorAddress,
      contractConfig: depositContractConfig
    });
  }, [recipientAddress, sourceChainId, destinationChainId, sourceToken, destinationToken, creatorAddress, depositContractConfig, createOrder, isPureTransfer]);
  // Call onSuccess when order is executed
  useOnOrderSuccess({
    orderData: oat,
    orderId,
    onSuccess
  });
  // For pure transfers, always use recipient address; for orders, use global address
  const displayAddress = isPureTransfer ? recipientAddress : globalAddress || recipientAddress;
  // Generate EIP-681 payment URI for the QR code so wallets know which chain/token to use
  const qrValue = getPaymentUrl(displayAddress, undefined, sourceToken.address === ZERO_ADDRESS ? "ETH" : sourceToken.address, sourceChainId, sourceToken.decimals);
  const handleCopyAddress = async () => {
    if (displayAddress) {
      await navigator.clipboard.writeText(displayAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleBack = () => {
    setCopied(false);
    onBack?.();
  };
  const handleClose = () => {
    setCopied(false);
    onClose?.();
  };
  // Show transfer result screen for completed pure transfers
  if (isPureTransfer && transferResult) {
    return _jsx(TransferResultScreen, {
      mode: mode,
      transferResult: transferResult,
      token: sourceToken,
      chainId: sourceChainId,
      recipientAddress: recipientAddress,
      onBack: onBack,
      onClose: onClose
    });
  }
  // Show order details if order has deposits or is being processed
  if (oat?.data && oat.data.depositTxs && oat.data.depositTxs.length > 0) {
    return _jsx("div", {
      className: classes?.orderDetailsContainer || cn("anyspend-container anyspend-qr-order-details font-inter bg-as-surface-primary mx-auto w-full max-w-[460px] p-6", mode === "page" && "border-as-border-secondary overflow-hidden rounded-2xl border shadow-xl"),
      children: _jsx("div", {
        className: classes?.orderDetailsContent || "anyspend-qr-order-details-content relative flex flex-col gap-4",
        children: _jsx(OrderDetails, {
          mode: mode,
          order: oat.data.order,
          depositTxs: oat.data.depositTxs,
          relayTxs: oat.data.relayTxs,
          executeTx: oat.data.executeTx,
          refundTxs: oat.data.refundTxs,
          onBack: handleBack
        })
      })
    });
  }
  // Show loading state while creating order (but not if we already have an orderId or for pure transfers)
  if (isCreatingOrder && !orderId && !isPureTransfer) {
    return _jsx("div", {
      className: classes?.loadingContainer || cn("anyspend-container anyspend-qr-loading font-inter bg-as-surface-primary mx-auto w-full max-w-[460px] p-6", mode === "page" && "border-as-border-secondary overflow-hidden rounded-2xl border shadow-xl"),
      children: _jsxs("div", {
        className: classes?.loadingContent || "anyspend-qr-loading-content flex flex-col items-center justify-center gap-4 py-12",
        children: [_jsx(Loader2, {
          className: classes?.loadingSpinner || "anyspend-qr-loading-spinner text-as-brand h-8 w-8 animate-spin"
        }), _jsx("p", {
          className: classes?.loadingText || "anyspend-qr-loading-text text-as-secondary text-sm",
          children: "Creating deposit order..."
        })]
      })
    });
  }
  return _jsx("div", {
    className: classes?.container || cn("anyspend-container anyspend-qr-deposit font-inter bg-as-surface-primary mx-auto w-full max-w-[460px] p-6", mode === "page" && "border-as-border-secondary overflow-hidden rounded-2xl border shadow-xl"),
    children: _jsxs("div", {
      className: classes?.content || "anyspend-qr-deposit-content flex flex-col gap-4",
      children: [_jsxs("div", {
        className: classes?.header || "anyspend-qr-header flex items-center justify-between",
        children: [_jsx("button", {
          onClick: handleBack,
          className: classes?.backButton || "anyspend-qr-back-button text-as-secondary hover:text-as-primary",
          children: _jsx("svg", {
            className: "h-5 w-5",
            fill: "none",
            viewBox: "0 0 24 24",
            stroke: "currentColor",
            children: _jsx("path", {
              strokeLinecap: "round",
              strokeLinejoin: "round",
              strokeWidth: 2,
              d: "M15 19l-7-7 7-7"
            })
          })
        }), _jsx("h2", {
          className: classes?.title || "anyspend-qr-title text-as-primary text-base font-semibold",
          children: "Deposit"
        }), onClose ? _jsx("button", {
          onClick: handleClose,
          className: classes?.closeButton || "anyspend-qr-close-button text-as-secondary hover:text-as-primary",
          children: _jsx("svg", {
            className: "h-5 w-5",
            fill: "none",
            viewBox: "0 0 24 24",
            stroke: "currentColor",
            children: _jsx("path", {
              strokeLinecap: "round",
              strokeLinejoin: "round",
              strokeWidth: 2,
              d: "M6 18L18 6M6 6l12 12"
            })
          })
        }) : _jsx("div", {
          className: "w-5"
        })]
      }), _jsxs("div", {
        className: classes?.tokenSelectorContainer || "anyspend-qr-token-selector flex flex-col gap-1.5",
        children: [_jsx("label", {
          className: classes?.tokenSelectorLabel || "anyspend-qr-token-label text-as-secondary text-sm",
          children: "Send"
        }), _jsx(TokenSelector, {
          chainIdsFilter: getAvailableChainIds("from"),
          context: "from",
          fromChainWalletVMSupported: true,
          isValidAddress: true,
          lockedChainIds: getAvailableChainIds("from"),
          multiWalletSupportEnabled: true,
          onAnalyticEvent: undefined,
          setToken: handleTokenSelect,
          supportedWalletVMs: ["evm"],
          token: undefined,
          trigger: _jsxs(Button, {
            variant: "outline",
            role: "combobox",
            className: classes?.tokenSelectorTrigger || "anyspend-qr-token-trigger border-as-stroke bg-as-surface-secondary flex h-auto w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5",
            children: [_jsxs("div", {
              className: "flex items-center gap-2",
              children: [sourceToken.metadata?.logoURI ? _jsx(ChainTokenIcon, {
                chainUrl: ALL_CHAINS[sourceChainId]?.logoUrl,
                tokenUrl: sourceToken.metadata.logoURI,
                className: "h-8 min-h-8 w-8 min-w-8"
              }) : _jsx("div", {
                className: "h-8 w-8 rounded-full bg-gray-700"
              }), _jsxs("div", {
                className: "flex flex-col items-start gap-0",
                children: [_jsx("div", {
                  className: "text-as-primary font-semibold",
                  children: sourceToken.symbol
                }), _jsx("div", {
                  className: "text-as-primary/70 text-xs",
                  children: ALL_CHAINS[sourceChainId]?.name ?? "Unknown"
                })]
              })]
            }), _jsx(ChevronsUpDown, {
              className: "h-4 w-4 shrink-0 opacity-70"
            })]
          })
        })]
      }), _jsxs("div", {
        className: classes?.qrContent || "anyspend-qr-content border-as-stroke flex items-start gap-4 rounded-xl border p-4",
        children: [_jsxs("div", {
          className: classes?.qrCodeContainer || "anyspend-qr-code-container flex flex-col items-center gap-2",
          children: [_jsx("div", {
            className: classes?.qrCode || "anyspend-qr-code rounded-lg bg-white p-2",
            children: _jsx(QRCodeSVG, {
              value: qrValue,
              size: 120,
              level: "M",
              marginSize: 0
            })
          }), _jsxs("span", {
            className: classes?.qrScanHint || "anyspend-qr-scan-hint text-as-secondary text-xs",
            children: ["SCAN WITH ", _jsx("span", {
              className: "inline-block",
              children: "\uD83E\uDD8A"
            })]
          })]
        }), _jsxs("div", {
          className: classes?.addressContainer || "anyspend-qr-address-container flex flex-1 flex-col gap-1",
          children: [_jsx("span", {
            className: classes?.addressLabel || "anyspend-qr-address-label text-as-secondary text-sm",
            children: "Deposit address:"
          }), _jsxs("div", {
            className: classes?.addressRow || "anyspend-qr-address-row flex items-start gap-1",
            children: [_jsx("span", {
              className: classes?.address || "anyspend-qr-address text-as-primary break-all font-mono text-sm leading-relaxed",
              children: displayAddress
            }), _jsx("button", {
              onClick: handleCopyAddress,
              className: classes?.addressCopyIcon || "anyspend-qr-copy-icon text-as-secondary hover:text-as-primary mt-0.5 shrink-0",
              children: copied ? _jsx(Check, {
                className: "h-4 w-4"
              }) : _jsx(Copy, {
                className: "h-4 w-4"
              })
            })]
          })]
        })]
      }), _jsx(ChainWarningText, {
        chainId: destinationChainId
      }), _jsxs(WarningText, {
        children: ["Only send ", sourceToken.symbol, " on ", ALL_CHAINS[sourceChainId]?.name ?? "the specified chain", ". Other tokens will not be converted."]
      }), isPureTransfer && isWatchingTransfer && _jsxs("div", {
        className: classes?.watchingIndicator || "anyspend-qr-watching flex items-center justify-center gap-2 rounded-lg bg-blue-500/10 p-3",
        children: [_jsx(Loader2, {
          className: "h-4 w-4 animate-spin text-blue-500"
        }), _jsx("span", {
          className: "text-sm text-blue-500",
          children: "Watching for incoming transfer..."
        })]
      }), _jsx("button", {
        onClick: handleCopyAddress,
        className: classes?.copyButton || "anyspend-qr-copy-button flex w-full items-center justify-center gap-2 rounded-xl bg-blue-500 py-3.5 font-medium text-white transition-all hover:bg-blue-600",
        children: "Copy deposit address"
      })]
    })
  });
}